import { secondaryStreamingApiConnector } from 'apiConnector/secondary-market/streaming';
import type { KeepAliveProductIdsSelector } from 'epic/secondaryMarket/realtimeEpic';
import { ofType } from 'redux-observable';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import {
  secondaryMarketPriceCleared,
  secondaryMarketPriceSubscribed,
  secondaryMarketPriceSubscribeFailed,
  secondaryMarketPriceUnsubscribeFailed,
} from 'state/secondaryMarket/realtime/realtime.action';
import { toBcsProductKey } from 'state/secondaryMarket/utils';
import { getBcsProductKeyByWorkspace } from 'state/selectors/common/tabs';
import type { EpicWithThunk } from 'state/state.common';
import { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';

import { waitUntilConnectedToSignalR } from '../utils';

export function subscribeLimitOrderStreamingEpic(): EpicWithThunk {
  return (action$, state$, { httpClient }) => {
    const { subscribe } = secondaryStreamingApiConnector(httpClient);
    const trigger$ = action$.pipe(
      ofType('SECONDARY_MARKET_LIMIT_ORDER_INIT_FORM_FETCHED', 'SECONDARY_MARKET_LIMIT_ORDER_RELOAD_FORM_FETCHED'),
    );

    return waitUntilConnectedToSignalR(trigger$, action$).pipe(
      withLatestFrom(state$),
      mergeMap(([{ tabId, workspace }, state]) => {
        const productKey: BcsProductKey | undefined = getBcsProductKeyByWorkspace(state, workspace, tabId);
        if (productKey === undefined) {
          return EMPTY;
        }

        return subscribe({ productIds: [productKey], backend: 'Icom' }).pipe(
          map(() => secondaryMarketPriceSubscribed(productKey!)),
          catchError(() => of(secondaryMarketPriceSubscribeFailed(productKey!))),
        );
      }),
    );
  };
}
export function unsubscribeLimitOrderStreamingEpic(
  ...keepAliveProductIdsSelectors: KeepAliveProductIdsSelector[]
): EpicWithThunk {
  return (action$, state$, { httpClient }) => {
    const { unsubscribe } = secondaryStreamingApiConnector(httpClient);
    return action$.pipe(
      ofType('SECONDARY_MARKET_QUOTES_WORKSPACE_REMOVE_TAB'),
      filter(a => {
        if (a.type === 'SECONDARY_MARKET_QUOTES_WORKSPACE_REMOVE_TAB') {
          return a.tabType === 'limitOrder';
        }
        return true;
      }),
      withLatestFrom(state$),
      mergeMap(([{ productKey }, state]) => {
        if (keepAliveProductIdsSelectors.some(selector => selector(state).has(productKey))) {
          return EMPTY;
        }
        const bcsProductKey = toBcsProductKey(productKey)!;
        return unsubscribe({ productIds: [bcsProductKey], backend: 'Icom' }).pipe(
          map(() => secondaryMarketPriceCleared(bcsProductKey)),
          catchError(() => of(secondaryMarketPriceUnsubscribeFailed(bcsProductKey))),
        );
      }),
    );
  };
}
